/**
 * Reconnect Service
 * Handles automatic silent WebSocket reconnection after unexpected disconnects.
 * Retries up to RECONNECT_MAX_ATTEMPTS times with exponential backoff before
 * giving up and delegating to the "Connection Lost" modal.
 *
 * The reconnect is "soft" only in terms of UX — the player sees a non-blocking
 * "Reconnecting..." indicator instead of a hard blocking modal. The server always
 * sends the full join snapshot on rejoin, so all client state is re-synced
 * cleanly — and the game is back only once that snapshot has landed: until
 * then the screen shows the game as it stood before the drop, and a command
 * pressed again on it could be carried out twice, so the reconnect lasts until
 * the snapshot's end, and the command gate sends nothing meanwhile.
 *
 * It ends one of three ways: rejoined; failed, every attempt spent or the
 * match gone, which no attempt can rejoin; or
 * stopped — the player left (nakamaService.disconnect), or a ban ended the
 * session (nakamaService.stopReconnecting). Stopped is at once and for good:
 * the attempt on its way is let go, nothing rejoins in the background, and
 * no socket is left open. A failed attempt closes its socket too.
 */

import { nakamaService } from './nakama-service';
import { useMatchStore } from '../../domains/match';
import { isMatchGone } from '../../domains/join-refusals';
import { expectOwnJoin, type OwnJoinWait } from '../../domains/players';

const RECONNECT_MAX_ATTEMPTS = 5;
/** Base delay in ms; actual delay for attempt N is BASE * 2^(N-1): 1s, 2s, 4s, 8s, 16s */
const RECONNECT_BASE_DELAY_MS = 1000;
/**
 * How long a rejoined socket waits for its join's snapshot. The server sends
 * it in the join itself, so it lands within a moment; past this, the game
 * goes on without the wait rather than hold the player for good.
 */
const SNAPSHOT_WAIT_MS = 10_000;

/** How a reconnect ended: back in the match, every attempt spent, or stopped by a leave or a ban. */
type ReconnectOutcome = 'rejoined' | 'failed' | 'stopped';

/** What ended a wait inside a reconnect, besides what it waited for. */
type Interruption = 'stopped' | 'dropped' | 'elapsed';

/**
 * One reconnect under way: whether it has been stopped, and the one wait it
 * may be in — a backoff between attempts, or a rejoin waiting for its
 * snapshot — which a stop, or the rejoined socket dropping, ends at once.
 */
class ReconnectRun {
  stopped = false;
  private interrupt: ((why: Interruption) => void) | null = null;

  stop(): void {
    this.stopped = true;
    this.interrupt?.('stopped');
  }

  drop(): void {
    this.interrupt?.('dropped');
  }

  /** Waits for `settled`, at most `ms` — or only `ms`, with nothing to wait for — unless stopped or dropped first. */
  wait(settled: Promise<void> | null, ms: number): Promise<'settled' | Interruption> {
    if (this.stopped) return Promise.resolve('stopped');
    return new Promise((resolve) => {
      let ended = false;
      const end = (how: 'settled' | Interruption) => {
        if (ended) return;
        ended = true;
        clearTimeout(timer);
        if (this.interrupt === end) this.interrupt = null;
        resolve(how);
      };
      const timer = setTimeout(() => end('elapsed'), ms);
      this.interrupt = end;
      settled?.then(() => end('settled'));
    });
  }
}

class ReconnectService {
  /** The reconnect under way, or null. Cleared the moment it is stopped. */
  private run: ReconnectRun | null = null;

  /**
   * True from a drop's first attempt until it ends: the socket is closed
   * meanwhile, or new and not back in the match until its join's snapshot
   * has landed.
   */
  inProgress(): boolean {
    return this.run !== null;
  }

  /**
   * Stops the reconnect under way, at once and for good: no attempt after
   * this, the one on its way let go and its socket closed as it answers. The
   * player leaving and a ban both stop it this way.
   */
  cancel(): void {
    if (!this.run) return;
    console.warn('[ReconnectService] Stopping the reconnect under way');
    this.run.stop();
    this.run = null;
  }

  /** The socket a rejoin is waiting on dropped before its snapshot landed: that attempt has failed. */
  noteDropped(): void {
    this.run?.drop();
  }

  /**
   * Attempt to silently reconnect the player to the match.
   * Creates a new socket, reconnects with the existing session, rejoins the
   * last known match, and waits for the join's snapshot, which re-syncs all
   * domain stores (airports, segments, fleets, etc.) as it lands.
   * The player sees a non-blocking indicator while this happens; the blocking
   * "Connection Lost" modal is only shown if all attempts fail.
   */
  async attemptReconnect(): Promise<ReconnectOutcome> {
    if (this.run) {
      console.warn('[ReconnectService] Already attempting reconnect, ignoring duplicate call');
      return 'stopped';
    }

    const run = new ReconnectRun();
    this.run = run;
    try {
      return await this.reconnect(run);
    } finally {
      if (this.run === run) this.run = null;
    }
  }

  private async reconnect(run: ReconnectRun): Promise<ReconnectOutcome> {
    const session = nakamaService.getSession();
    const matchId = useMatchStore.getState().matchId;

    if (!session || !matchId) {
      console.warn('[ReconnectService] Missing session or matchId — cannot reconnect silently');
      return 'failed';
    }

    // A stop — the player leaving, a ban — comes through cancel() and marks
    // the run, whatever the loop is waiting on as it comes.
    for (let attempt = 1; attempt <= RECONNECT_MAX_ATTEMPTS; attempt++) {
      if (run.stopped) {
        console.warn('[ReconnectService] Stopped before attempt', attempt);
        return 'stopped';
      }

      console.log(`[ReconnectService] Attempt ${attempt}/${RECONNECT_MAX_ATTEMPTS}...`);
      // Create a fresh socket and connect with the existing authenticated session
      const socket = nakamaService.createSocket();
      let snapshot: OwnJoinWait | null = null;
      try {
        await nakamaService.connect(session);
        if (run.stopped) {
          nakamaService.closeSocket(socket);
          return 'stopped';
        }

        // Rejoin the match. The server sends the full join snapshot (airports,
        // segments, fleets, etc.), and the game is back once it has landed:
        // its last message, the player's own join, is noticed as it is handled.
        snapshot = expectOwnJoin();
        await nakamaService.joinMatch(matchId);
        const waited = await run.wait(snapshot.landed, SNAPSHOT_WAIT_MS);
        snapshot.forget();
        if (run.stopped) {
          nakamaService.closeSocket(socket);
          return 'stopped';
        }
        if (waited === 'dropped') throw new Error("the socket dropped before the join's snapshot landed");
        if (waited === 'elapsed') {
          console.warn(`[ReconnectService] No join snapshot within ${SNAPSHOT_WAIT_MS}ms — going on without it`);
        }

        console.log('[ReconnectService] ✅ Reconnected successfully');
        return 'rejoined';
      } catch (err) {
        console.warn(`[ReconnectService] Attempt ${attempt} failed:`, err);
        // A failed attempt leaves nothing open: its socket would otherwise
        // stay connected, and its close later read as a drop.
        snapshot?.forget();
        nakamaService.closeSocket(socket);
        // Stopped while it was on its way — the last attempt too, which has
        // no backoff after it to notice — it has not failed: the player left.
        if (run.stopped) return 'stopped';
        // The match is not there — over, or lost as the server restarted — and
        // no attempt can rejoin it: Connection lost opens now, not after every
        // backoff, and its Reconnect takes the player back to the way in, which
        // asks the server why.
        if (isMatchGone(err)) {
          console.warn('[ReconnectService] The match is gone: no attempt can rejoin it');
          return 'failed';
        }

        if (attempt < RECONNECT_MAX_ATTEMPTS) {
          const delay = RECONNECT_BASE_DELAY_MS * Math.pow(2, attempt - 1);
          console.log(`[ReconnectService] Retrying in ${delay}ms...`);
          if ((await run.wait(null, delay)) === 'stopped') return 'stopped';
        }
      }
    }

    console.error('[ReconnectService] ❌ All reconnect attempts exhausted');
    return 'failed';
  }
}

export const reconnectService = new ReconnectService();
